// client/src/hooks/useChat.js

import { useState, useEffect, useRef } from 'react'
import ChatService from '../services/ChatService'
import useAuth from './useAuth'

/**
 * Custom hook for managing chat WebSocket connection and related state
 * @param {Function} onMessage - Callback for handling new messages
 * @param {Function} onError - Callback for handling chat errors
 * @returns {Object} Chat connection state and controls
 */
export default function useChat(onMessage, onError) {
  const { user, isLoggedIn } = useAuth()
  const [chatConnected, setChatConnected] = useState(false)

  // Keep latest callbacks in refs so we don't have to rewire the socket listeners
  const latestOnMessageRef = useRef(onMessage)
  const latestOnErrorRef = useRef(onError)

  useEffect(() => {
    latestOnMessageRef.current = onMessage
  }, [onMessage])

  useEffect(() => {
    latestOnErrorRef.current = onError
  }, [onError])

  // Connect to chat service and set up event handlers
  // Only react to auth state changes (login/logout or identity change),
  // not any change to the user object shape to avoid unnecessary disconnects.
  useEffect(() => {
    if (!isLoggedIn || !user) return

    let isMounted = true
    // Track unsubscribe handlers for this effect run
    let unsubscribeMessage = null
    let unsubscribeError = null

    const connectToChat = async () => {
      try {
        await ChatService.connect()
        if (isMounted) {
          setChatConnected(true)
        }
      } catch (err) {
        console.error('Failed to connect to chat:', err)
        if (isMounted && latestOnErrorRef.current) {
          latestOnErrorRef.current('Failed to connect to chat')
        }
        return
      }

      // Stable wrapper handlers that pull latest callbacks from refs
      const messageWrapper = (message, action) => {
        const handler = latestOnMessageRef.current
        if (typeof handler === 'function') {
          try {
            handler(message, action)
          } catch (e) {
            console.error('Error in onMessage handler:', e)
          }
        }
      }

      const errorWrapper = (e) => {
        const handler = latestOnErrorRef.current
        if (typeof handler === 'function') {
          try {
            handler(e?.message || 'Chat error')
          } catch (err) {
            console.error('Error in onError handler:', err)
          }
        } else {
          console.error('Chat service error:', e)
        }
      }

      // Register wrappers once per connection
      if (typeof ChatService.onMessage === 'function') {
        unsubscribeMessage = ChatService.onMessage(messageWrapper)
      }
      if (typeof ChatService.onError === 'function') {
        unsubscribeError = ChatService.onError(errorWrapper)
      }
    }

    connectToChat()

    // Handle app resume (iOS/Android) and visibility changes (web)
    const handleAppResume = () => {
      if (isLoggedIn && !ChatService.isConnected) {
        console.log('App resumed - reconnecting chat')
        connectToChat()
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleAppResume()
      }
    }

    // Capacitor/Cordova 'resume' event for mobile apps
    document.addEventListener('resume', handleAppResume)
    // Standard visibility API for web browsers
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      isMounted = false

      // Remove event listeners
      document.removeEventListener('resume', handleAppResume)
      document.removeEventListener('visibilitychange', handleVisibilityChange)

      // Clean up subscriptions captured in this effect
      try {
        if (typeof unsubscribeMessage === 'function') {
          unsubscribeMessage()
        }
      } catch {
        // Ignore cleanup errors - component is unmounting
      }

      try {
        if (typeof unsubscribeError === 'function') {
          unsubscribeError()
        }
      } catch {
        // Ignore cleanup errors - component is unmounting
      }

      try {
        if (typeof ChatService.disconnect === 'function') {
          ChatService.disconnect()
        }
      } catch {
        // Ignore cleanup errors - component is unmounting
      }

      setChatConnected(false)
    }
  }, [isLoggedIn, user])

  return {
    chatConnected,
    isConnected: chatConnected && ChatService.isConnected,
  }
}
